import { EnvService } from '@aipb/gulliver/env/util';
import { PersonalContentPermissionService } from '@aipb/gulliver/personal-content/auth';
import {
  FavoriteCollectionService,
  FavoriteLibService,
  FavoriteUserRepository,
  FavoriteUserService
} from '@aipb/gulliver/personal-content/data-access';
import { PersonalContentEnvService } from '@aipb/gulliver/personal-content/env';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, filter, forkJoin, mergeMap } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { PluginFavoriteListComponent } from '../plugin-favorite-list/plugin-favorite-list.component';

@UntilDestroy()
@Component({
  selector: 'pc-plugin',
  templateUrl: './personal-content-plugin.component.html',
  styleUrls: ['./personal-content-plugin.component.scss'],
  standalone: true,
  imports: [CommonModule, MatTabsModule, TranslocoModule, PluginFavoriteListComponent]
})
export class PersonalContentPluginComponent implements OnInit {
  @Input() compact = true;

  favoriteCollections$ = this.favoriteUserRepository.favoriteCollections$;
  generalCollections$ = this.favoriteUserRepository.generalCollections$;
  didacticCollections$ = this.favoriteUserRepository.didacticCollections$;
  trashbinCollections$ = this.favoriteUserRepository.trashbinCollections$;
  loading$ = this.favoriteUserRepository.loading$;

  constructor(
    private favoriteUserService: FavoriteUserService,
    private favoriteCollectionService: FavoriteCollectionService,
    private favoriteLibService: FavoriteLibService,
    private env: EnvService,
    private personalContentPermission: PersonalContentPermissionService,
    private personalContentEnv: PersonalContentEnvService,
    private favoriteUserRepository: FavoriteUserRepository
  ) {}

  get hideCollections(): boolean {
    return !this.personalContentPermission.canUseCollections;
  }

  get hideDidacticCollections(): boolean {
    return this.personalContentEnv.env.hideDidacticCollections;
  }

  ngOnInit(): void {
    this.init();

    combineLatest([
      this.favoriteUserRepository.favoriteCollectionId$,
      this.favoriteUserRepository.trashbinCollectionId$,
      this.favoriteLibService.refreshPlugin$.pipe(startWith(null))
    ])
      .pipe(
        filter(([favoriteCollectionId, trashbinCollectionId]) => !!favoriteCollectionId && !!trashbinCollectionId),
        mergeMap(([favoriteCollectionId, trashbinCollectionId]) =>
          forkJoin([
            this.favoriteCollectionService.get(favoriteCollectionId),
            this.favoriteCollectionService.get(trashbinCollectionId)
          ])
        ),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.init();
      });
  }

  private init(): void {
    if (
      !this.env.isPersonalContentEnabled ||
      (!this.personalContentPermission.canUseCollections && !this.personalContentPermission.canUsePersonalContent)
    )
      return;

    this.favoriteUserService.getInfo().pipe(untilDestroyed(this)).subscribe();
  }
}
